import * as Sentry from "@sentry/react";
import { useSnackbar } from "notistack";

import { useTranslation } from "react-i18next";
import { useCargosTable } from "../context/CargoTableContext";
import {
	useAddTimocomRefMutation,
	useAddTransRefMutation,
	useReportChunkCompanionErrorMutation,
	useReportTimocomCompanionErrorMutation,
	useReportTransCompanionErrorMutation,
} from "../features/cargo/cargoApiSlice";
import { sendCargosToCompanion } from "../utils/companion";

export function useAddViaCompanion() {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const {
		setTransCompanionStatusForCargo,
		setTransCompanionStatusForCargos,
		setTimocomCompanionStatusForCargo,
		setTimocomCompanionStatusForCargos,
	} = useCargosTable();

	const [addTransRef] = useAddTransRefMutation(); // triggered in background so no loading state needed
	const [addTimocomRef] = useAddTimocomRefMutation(); // triggered in background so no loading state needed
	const [reportChunkCompanionError] = useReportChunkCompanionErrorMutation();
	const [reportCompanionErrorTrans] = useReportTransCompanionErrorMutation();
	const [reportCompanionErrorTimocom] =
		useReportTimocomCompanionErrorMutation();

	function addViaCompanion(payload, isRetry = false) {
		// companion app
		// send event to companion app
		if (
			payload.some((cargo) => cargo.postOnTrans) ||
			payload.some((cargo) => cargo.postOnTimocom)
		) {
			const cargosToPostOnTrans = payload.filter((cargo) => cargo.postOnTrans);

			const cargosToPostOnTimocom = payload.filter(
				(cargo) => cargo.postOnTimocom,
			);

			const cargoIdsTrans = cargosToPostOnTrans.map((c) => c.id);
			const cargoIdsTimocom = cargosToPostOnTimocom.map((c) => c.id);

			// If sending fails mid-way, don't report already-successful cargos as failed.
			const successfulCargoIdsTrans = new Set();
			const successfulCargoIdsTimocom = new Set();

			// local overlay so UI can update even before cargos list refreshes
			const statusToSet = isRetry ? "retrying" : "pending";

			setTransCompanionStatusForCargos(
				cargosToPostOnTrans.map((c) => c.id),
				statusToSet,
			);

			setTimocomCompanionStatusForCargos(
				cargosToPostOnTimocom.map((c) => c.id),
				statusToSet,
			);

			// best-effort: if companion isn't running, don't block saving cargos
			void sendCargosToCompanion(
				{
					trans: cargosToPostOnTrans,
					timocom: cargosToPostOnTimocom,
				},
				{
					onEvent: async (event) => {
						const platforms = {
							"trans.cargoStatusChanged": {
								name: "Trans.eu",
								setStatus: setTransCompanionStatusForCargo,
								addRef: addTransRef,
								reportError: reportCompanionErrorTrans,
								successSet: successfulCargoIdsTrans,
							},
							"timocom.cargoStatusChanged": {
								name: "Timocom",
								setStatus: setTimocomCompanionStatusForCargo,
								addRef: addTimocomRef,
								reportError: reportCompanionErrorTimocom,
								successSet: successfulCargoIdsTimocom,
							},
						};

						const platform = platforms[event.type];
						if (!platform) return;

						const { status, cargo, referenceNumber } = event.payload;
						const cargoId = cargo?.id;
						const cargoUuid = cargo?.uuid;

						const statusMessages = {
							processing: {
								text: t("companion_started_processing", {
									cargoUuid,
									platformName: platform.name,
								}),
								variant: "info",
							},
							error: {
								text: t("companion_adding_error", {
									cargoUuid,
									platformName: platform.name,
								}),
								variant: "error",
							},
							success: {
								text: t("companion_adding_success", {
									cargoUuid,
									platformName: platform.name,
								}),
								variant: "success",
							},
						};

						const message = statusMessages[status];
						if (!message) return;

						enqueueSnackbar(message.text, { variant: message.variant });

						if (cargoId != null) {
							platform.setStatus(cargoId, status);
						}

						if (status === "success" && cargoId != null) {
							platform.successSet.add(cargoId);
							platform.setStatus(cargoId, "success");

							try {
								await platform
									.addRef({
										cargoId,
										referenceNumber,
										type: "add",
									})
									.unwrap();
							} catch (err) {
								console.error(
									`Failed to save ${platform.name} reference number for cargo.`,
									err,
								);

								Sentry.captureMessage(
									`Failed to link ${platform.name} cargo (${referenceNumber}) with system cargo (${cargoId})`,
									"error",
									{
										extra: {
											cargoId,
											referenceNumber,
											platform: platform.name,
										},
									},
								);

								enqueueSnackbar(
									t("companion_link_error", {
										platformName: platform.name,
									}),
									{ variant: "error" },
								);
							}
						}

						if (status === "error" && cargoId != null) {
							platform.setStatus(cargoId, "error");

							try {
								await platform
									.reportError({
										cargoId,
										type: "add",
									})
									.unwrap();
							} catch (err) {
								console.error(
									`Failed to report ${platform.name} post error for cargo.`,
									err,
								);

								Sentry.captureMessage(
									`Failed to report ${platform.name} post error for cargo ${cargoId}`,
									"error",
									{
										extra: {
											cargoId,
											platform: platform.name,
										},
									},
								);

								enqueueSnackbar(
									t("companion_report_error_error", {
										platformName: platform.name,
									}),
									{ variant: "error" },
								);
							}
						}
					},
				},
			)
				.then((ack) => {
					const count =
						ack && typeof ack === "object" && "count" in ack
							? ack.count
							: cargosToPostOnTrans.length + cargosToPostOnTimocom.length;

					enqueueSnackbar(t("companion_finished_adding", { count }), {
						variant: "success",
					});
				})
				.catch(async (_error) => {
					const message = t("companion_top_level_error");
					enqueueSnackbar(message, {
						variant: "error",
					});

					// report to backend that cargos were not posted, so they can be posted later when companion is running
					const cargoIdsTransToReport = cargoIdsTrans.filter(
						(id) => !successfulCargoIdsTrans.has(id),
					);
					const cargoIdsTimocomToReport = cargoIdsTimocom.filter(
						(id) => !successfulCargoIdsTimocom.has(id),
					);

					if (
						cargoIdsTransToReport.length === 0 &&
						cargoIdsTimocomToReport.length === 0
					) {
						return;
					}

					setTransCompanionStatusForCargos(cargoIdsTransToReport, "error");
					setTimocomCompanionStatusForCargos(cargoIdsTimocomToReport, "error");

					try {
						await reportChunkCompanionError({
							cargoIdsTrans: cargoIdsTransToReport,
							cargoIdsTimocom: cargoIdsTimocomToReport,
							type: "add",
						}).unwrap();
					} catch (error) {
						const reportMessage =
							error instanceof Error
								? error.message
								: "Failed to report post error to backend.";

						enqueueSnackbar(t("companion_failed_mass_report"), {
							variant: "error",
						});
						console.error(
							"Failed to report post error to backend.",
							reportMessage,
						);
						Sentry.captureMessage(
							"Failed to report companion post error to backend",
							"error",
							{
								extra: {
									cargoIdsTrans: cargoIdsTransToReport,
									cargoIdsTimocom: cargoIdsTimocomToReport,
									reportMessage: reportMessage,
								},
							},
						);
					}
				});
		}
	}

	return { addViaCompanion };
}
